import React from "react";
import { motion } from "framer-motion";
import { 
  Check, 
  X, 
  Zap,
  Phone,
  FileText,
  MessageSquare,
  Award,
  Calendar,
  Shield,
  TrendingUp,
  DollarSign,
  Briefcase,
  Layers,
  LayoutGrid,
  CreditCard,
  Sparkles
} from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "../ui/table";
import { Card } from "../ui/card";

const AshaVsLassieSlide = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };

  const features = [
    { 
      name: "EOB Posting",
      asha: true, 
      lassie: true, 
      icon: Check 
    },
    { 
      name: "Claim Submission",
      asha: true, 
      lassie: false, 
      icon: FileText 
    },
    { 
      name: "Claim Scrubbing",
      asha: true, 
      lassie: false, 
      icon: Shield 
    },
    { 
      name: "Insurance Verification",
      asha: true, 
      lassie: false, 
      icon: Phone 
    },
    { 
      name: "Payments",
      asha: true, 
      lassie: false, 
      icon: CreditCard 
    },
    { 
      name: "AI RCM Suite",
      asha: true, 
      lassie: false, 
      icon: Sparkles 
    },
    { 
      name: "Denial Management",
      asha: true, 
      lassie: false, 
      icon: Award 
    },
    { 
      name: "Patient Billing",
      asha: true, 
      lassie: false, 
      icon: MessageSquare 
    },
    { 
      name: "AR Management",
      asha: true, 
      lassie: false, 
      icon: TrendingUp 
    },
  ];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-8 lg:p-12 font-sans overflow-hidden">
      <motion.div 
        className="flex flex-row h-full gap-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left Column: Header + TLDR */}
        <div className="w-[35%] flex flex-col gap-6 justify-center">
            <motion.div variants={itemVariants} className="text-left">
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight leading-tight">
                Asha Health <span className="text-slate-400 font-light mx-2">vs</span> <br/>Lassie
              </h2>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white border border-blue-100 rounded-3xl p-8 shadow-lg shadow-blue-900/5 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-110 duration-500" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    <LayoutGrid className="w-6 h-6" />
                  </div>
                  <h3 className="text-blue-900 font-bold text-2xl">Bottom Line</h3>
                </div>
                
                <div className="space-y-4">
                  <p className="text-slate-700 leading-relaxed text-lg">
                    Lassie provides only <span className="font-extrabold text-blue-600 bg-blue-50 px-1 rounded">1/8</span> of the revenue cycle solution that Asha offers.
                  </p>
                  
                  <div className="h-px w-full bg-slate-100 my-4" />
                  
                  <p className="text-slate-700 leading-relaxed text-lg">
                    While Lassie is just a point solution for EOB posting, Asha is a complete <strong>All-in-One Platform</strong> including both Back Office <em>and</em> Front Office automation—something Lassie completely lacks.
                  </p>
                </div>
              </div>
            </motion.div>
        </div>

        {/* Right Column: Feature Stack Comparison */}
        <motion.div variants={itemVariants} className="w-[65%] flex flex-col h-full justify-center">
           <Card className="h-auto max-h-full border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col bg-white rounded-3xl ring-1 ring-slate-100">
              <div className="overflow-y-auto p-0 custom-scrollbar">
                  <Table>
                      <TableHeader className="bg-slate-50/80 backdrop-blur sticky top-0 z-10 shadow-sm">
                        <TableRow className="border-b border-slate-200 hover:bg-transparent">
                          <TableHead className="py-3 pl-8 font-bold text-lg text-slate-800 w-auto">Feature Breakdown</TableHead>
                          <TableHead className="w-32 text-center font-bold text-blue-600 text-lg py-3">Asha</TableHead>
                          <TableHead className="w-32 text-center font-bold text-orange-500 text-lg pr-8 py-3">Lassie</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                          {features.map((feature, idx) => (
                              <TableRow key={idx} className="hover:bg-blue-50/30 border-b-slate-100 last:border-0 transition-colors">
                                  <TableCell className="font-medium text-slate-700 text-base py-2 pl-8 flex items-center gap-4">
                                      <div className={`p-1.5 rounded-lg shadow-sm ${feature.asha && !feature.lassie ? 'bg-blue-100 text-blue-600 ring-1 ring-blue-200' : 'bg-slate-100 text-slate-500'}`}>
                                          <feature.icon className="w-4 h-4" />
                                      </div>
                                      {feature.name}
                                  </TableCell>
                                  <TableCell className="w-32 text-center">
                                      <div className="flex justify-center">
                                          {feature.asha ? (
                                              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center shadow-sm ring-2 ring-green-50">
                                                  <Check className="w-5 h-5 text-green-600 stroke-[3]" />
                                              </div>
                                          ) : (
                                              <div className="h-8 w-8 flex items-center justify-center">
                                                  <X className="w-5 h-5 text-slate-300" />
                                              </div>
                                          )}
                                      </div>
                                  </TableCell>
                                  <TableCell className="w-32 text-center pr-8">
                                      <div className="flex justify-center">
                                          {feature.lassie ? (
                                              <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center shadow-sm ring-2 ring-orange-50">
                                                  <Check className="w-5 h-5 text-orange-600 stroke-[3]" />
                                              </div>
                                          ) : (
                                              <div className="h-8 w-8 flex items-center justify-center">
                                                  <X className="w-5 h-5 text-red-200" />
                                              </div>
                                          )}
                                      </div>
                                  </TableCell>
                              </TableRow>
                          ))}
                          
                          {/* Pricing Header Row for visual separation */}
                          <TableRow className="bg-slate-50 border-t-2 border-slate-200 hover:bg-slate-50">
                              <TableCell colSpan={3} className="py-1.5 px-8 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                                  Pricing Comparison
                              </TableCell>
                          </TableRow>

                          {/* Setup Fee Row */}
                          <TableRow className="bg-slate-50/30 font-bold border-b border-slate-200 hover:bg-slate-50/50">
                              <TableCell className="text-base py-3 pl-8 flex items-center gap-4">
                                  <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-600 ring-1 ring-emerald-200 shadow-sm">
                                      <Layers className="w-4 h-4" />
                                  </div>
                                  Setup Fee
                              </TableCell>
                              <TableCell className="w-32 text-center">
                                  <div className="text-blue-600 text-xl font-black tracking-tight">$0</div>
                                  <div className="text-[10px] text-blue-600/60 font-semibold uppercase tracking-wide">Included</div>
                              </TableCell>
                              <TableCell className="w-32 text-center pr-8">
                                  <div className="text-orange-500 text-xl font-black tracking-tight">$1,500</div>
                                  <div className="text-[10px] text-orange-500/60 font-semibold uppercase tracking-wide">Upfront Cost</div>
                              </TableCell>
                          </TableRow>

                          {/* Transaction Fee Row */}
                          <TableRow className="bg-slate-50/80 font-bold hover:bg-slate-50">
                              <TableCell className="text-base py-3 pl-8 flex items-center gap-4">
                                  <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-600 ring-1 ring-emerald-200 shadow-sm">
                                      <DollarSign className="w-4 h-4" />
                                  </div>
                                  Transaction Fee
                              </TableCell>
                              <TableCell className="w-32 text-center">
                                  <div className="text-blue-600 text-xl font-black tracking-tight">3.5%</div>
                                  <div className="text-[10px] text-blue-600/60 font-semibold uppercase tracking-wide">Full Cycle</div>
                              </TableCell>
                              <TableCell className="w-32 text-center pr-8">
                                  <div className="text-orange-500 text-xl font-black tracking-tight">2%</div>
                                  <div className="text-[10px] text-orange-500/60 font-semibold uppercase tracking-wide">Posting Only</div>
                              </TableCell>
                          </TableRow>
                      </TableBody>
                  </Table>
              </div>
           </Card>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AshaVsLassieSlide;
